// ============================================================
// ✅ EditEmployeModal.jsx - Modification d'un employé
// Ordre : Société → Poste → Manager → Admin checkbox
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { X, User, Briefcase, Mail, AlertCircle } from 'lucide-react';

export default function EditEmployeModal({ isOpen, onClose, onSuccess, employe }) {
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('civil');
  const [postes, setPostes] = useState([]);
  const [managers, setManagers] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [formData, setFormData] = useState({
    ID_Employes: '', Matricule: '', Nom: '', Prenom: '', CIN: '',
    DateNaissance: '', DateEmbauche: '', Email: '', Telephone: '',
    StatutContrat: 'CDI', ID_Postes: '', ID_Societe: '',
    Nationalite: 'Marocaine', Sexe: 'M',
    SituationFamiliale: 'Célibataire', NombreEnfants: 0,
    ID_Manager: '', EstActif: true
  });

  const { user } = useUser();
  const isSuperAdmin = user?.EstSuperAdmin === 1 || user?.EstSuperAdmin === true;

  const tabs = [
    { id: 'civil', label: '👤 État civil', icon: <User size={16} /> },
    { id: 'pro', label: '💼 Professionnel', icon: <Briefcase size={16} /> },
    { id: 'contact', label: '📞 Contact', icon: <Mail size={16} /> }
  ];

  // ✅ Correction : useCallback pour fetchPostes
  const fetchPostes = useCallback(async (idSociete = null) => {
    try { 
      const societe = idSociete || formData.ID_Societe || user?.ID_Societe; 
      const res = await api.get('/postes', { params: { ID_Societe: isSuperAdmin ? (societe || null) : user?.ID_Societe } }); 
      setPostes(res.data.postes || []); 
    } catch (err) { console.error(err); }
  }, [formData.ID_Societe, user?.ID_Societe, isSuperAdmin]); // ✅ Dépendances

  // ✅ Correction : useCallback pour fetchManagers
  const fetchManagers = useCallback(async (idSociete = null) => {
    try { 
      const societe = idSociete || formData.ID_Societe || user?.ID_Societe; 
      const res = await api.get('/employes/managers', { 
        params: { exclude: employe?.ID_Employes, ID_Societe: isSuperAdmin ? (societe || null) : user?.ID_Societe } 
      }); 
      setManagers(res.data.managers || []); 
    } catch (err) { console.error(err); }
  }, [formData.ID_Societe, user?.ID_Societe, isSuperAdmin, employe?.ID_Employes]); // ✅ Dépendances

  const fetchSocietes = async () => { try { const res = await api.get('/societes'); setSocietes(res.data.societes || []); } catch (err) { console.error(err); } };

  // ✅ Correction : ajout de toutes les dépendances
  useEffect(() => {
    if (isOpen && employe) {
      setFormData({
        ID_Employes: employe.ID_Employes, Matricule: employe.Matricule || '',
        Nom: employe.Nom || '', Prenom: employe.Prenom || '', CIN: employe.CIN || '',
        DateNaissance: employe.DateNaissance || '', DateEmbauche: employe.DateEmbauche || '',
        Email: employe.Email || '', Telephone: employe.Telephone || '',
        StatutContrat: employe.StatutContrat || 'CDI', ID_Postes: employe.ID_Postes || '',
        ID_Societe: employe.ID_Societe || user?.ID_Societe || '',
        Nationalite: employe.Nationalite || 'Marocaine', Sexe: employe.Sexe || 'M',
        SituationFamiliale: employe.SituationFamiliale || 'Célibataire',
        NombreEnfants: employe.NombreEnfants || 0, ID_Manager: employe.ID_Manager || '',
        EstActif: employe.EstActif === 1 || employe.EstActif === true
      });
      setIsAdmin(employe.Role === 'admin');
      if (isSuperAdmin) fetchSocietes();
      fetchPostes(employe.ID_Societe);
      fetchManagers(employe.ID_Societe);
    }
  }, [isOpen, employe, isSuperAdmin, fetchPostes, fetchManagers]); // ✅ Dépendance corrigée

  const handleSocieteChange = (idSociete) => {
    setFormData({ ...formData, ID_Societe: idSociete, ID_Postes: '', ID_Manager: '' });
    if (idSociete) { fetchPostes(idSociete); fetchManagers(idSociete); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    const oldEmail = employe?.Email;
    let role = 'user';
    if (isAdmin) role = 'admin';
    else if (employe?.Role === 'manager') role = 'manager';
    else if (formData.ID_Manager && parseInt(formData.ID_Manager) === formData.ID_Employes) role = 'manager';

    try {
      const response = await api.put(`/employes/${formData.ID_Employes}`, { ...formData, Role: role, ID_Societe: isSuperAdmin ? formData.ID_Societe : employe.ID_Societe });
      if (response.data.success) {
        if (oldEmail && oldEmail !== formData.Email) { try { await api.post('/email/change-notification', { email: formData.Email, oldEmail, nom: formData.Nom, prenom: formData.Prenom, newEmail: formData.Email }); } catch (emailErr) { console.error(emailErr); } }
        onSuccess(); onClose();
      } else { setError(response.data.message); }
    } catch (err) { setError(err.response?.data?.message || 'Erreur'); }
    finally { setLoading(false); }
  };

  if (!isOpen || !employe) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-semibold text-blue-600">✏️ Modifier l'employé</h3><button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button></div>
        <div className="flex gap-1 border-b mb-4">{tabs.map(tab => (<button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 text-sm rounded-t-lg flex items-center gap-2 transition ${activeTab === tab.id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{tab.icon} {tab.label}</button>))}</div>
        {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          
          {activeTab === 'civil' && (
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Matricule *</label><input type="text" className="w-full p-2 border rounded-lg" value={formData.Matricule} onChange={(e) => setFormData({...formData, Matricule: e.target.value})} required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">CIN *</label><input type="text" className="w-full p-2 border rounded-lg" value={formData.CIN} onChange={(e) => setFormData({...formData, CIN: e.target.value})} required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label><input type="text" className="w-full p-2 border rounded-lg" value={formData.Nom} onChange={(e) => setFormData({...formData, Nom: e.target.value})} required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label><input type="text" className="w-full p-2 border rounded-lg" value={formData.Prenom} onChange={(e) => setFormData({...formData, Prenom: e.target.value})} required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label><input type="date" className="w-full p-2 border rounded-lg" value={formData.DateNaissance} onChange={(e) => setFormData({...formData, DateNaissance: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Nationalité</label><input type="text" className="w-full p-2 border rounded-lg" value={formData.Nationalite} onChange={(e) => setFormData({...formData, Nationalite: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Sexe</label><select className="w-full p-2 border rounded-lg" value={formData.Sexe} onChange={(e) => setFormData({...formData, Sexe: e.target.value})}><option value="M">Masculin</option><option value="F">Féminin</option></select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Situation familiale</label><select className="w-full p-2 border rounded-lg" value={formData.SituationFamiliale} onChange={(e) => setFormData({...formData, SituationFamiliale: e.target.value})}><option value="Célibataire">Célibataire</option><option value="Marié">Marié(e)</option><option value="Divorcé">Divorcé(e)</option><option value="Veuf">Veuf/Veuve</option></select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Nombre d'enfants</label><input type="number" className="w-full p-2 border rounded-lg" value={formData.NombreEnfants} onChange={(e) => setFormData({...formData, NombreEnfants: parseInt(e.target.value) || 0})} /></div>
            </div>
          )}

          {activeTab === 'pro' && (
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Date d'embauche *</label><input type="date" className="w-full p-2 border rounded-lg" value={formData.DateEmbauche} onChange={(e) => setFormData({...formData, DateEmbauche: e.target.value})} required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Statut contrat</label><select className="w-full p-2 border rounded-lg" value={formData.StatutContrat} onChange={(e) => setFormData({...formData, StatutContrat: e.target.value})}><option value="CDI">CDI</option><option value="CDD">CDD</option><option value="Stage">Stage</option><option value="Freelance">Freelance</option></select></div>
              
              {/* 1️⃣ Société */}
              {isSuperAdmin && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Société *</label>
                  <select className="w-full p-2 border rounded-lg" value={formData.ID_Societe || ''} onChange={(e) => handleSocieteChange(e.target.value)} required>
                    <option value="">Sélectionner une société</option>
                    {societes.map(s => <option key={s.ID_Societe} value={s.ID_Societe}>{s.RaisonSociale} ({s.Sigle})</option>)}
                  </select>
                </div>
              )}

              {/* 2️⃣ Poste */}
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Poste</label><select className="w-full p-2 border rounded-lg" value={formData.ID_Postes} onChange={(e) => setFormData({...formData, ID_Postes: e.target.value})}><option value="">Sélectionner un poste</option>{postes.map(p => <option key={p.ID_Postes} value={p.ID_Postes}>{p.Nom_Postes}</option>)}</select></div>

              {/* 3️⃣ Manager */}
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Manager (N+1)</label><select className="w-full p-2 border rounded-lg" value={formData.ID_Manager} onChange={(e) => setFormData({...formData, ID_Manager: e.target.value})}><option value="">Aucun</option><option value={formData.ID_Employes}>Soi-même (top niveau)</option><option disabled>──── Managers ────</option>{managers.map(m => <option key={m.ID_Employes} value={m.ID_Employes}>{m.Prenom} {m.Nom} {m.Role === 'admin' ? '👑' : '(Manager)'}</option>)}</select></div>
              
              {!isSuperAdmin && <div className="col-span-2 p-2.5 bg-gray-50 rounded-lg text-sm text-gray-600">Société : <strong>{user?.SocieteNom || `ID: ${user?.ID_Societe}`}</strong></div>}
              
              {/* 4️⃣ Admin checkbox */}
              <div className="col-span-2"><label className="flex items-center gap-2"><input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} /><span className="text-sm text-gray-700">Administrateur (accès total)</span></label><p className="text-xs text-gray-400 mt-1">⚠️ Prioritaire sur le rôle manager</p></div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Email *</label><input type="email" className="w-full p-2 border rounded-lg" value={formData.Email} onChange={(e) => setFormData({...formData, Email: e.target.value})} required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label><input type="tel" className="w-full p-2 border rounded-lg" value={formData.Telephone} onChange={(e) => setFormData({...formData, Telephone: e.target.value})} /></div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50">{loading ? 'Enregistrement...' : 'Modifier'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}